import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ShieldAlert } from 'lucide-react'

import { branchListQueryOptions } from '@/api/reference-api'
import { session } from '@/api/session'
import { useAuth } from '@/auth/use-auth'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { CUSTOMER_PERMISSIONS, useCustomerPermissions } from '@/features/customers/permissions'
import { CustomerWizard } from '@/features/customers/wizard/customer-wizard'

/**
 * Onboarding a customer.
 *
 * The record is created in draft. It becomes a customer who can hold accounts only when a
 * supervisor approves the KYC file, and that supervisor cannot be the person who submitted it.
 */
export function CustomerNewPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const permissions = useCustomerPermissions()
  const branches = useQuery(branchListQueryOptions())

  const branchList = branches.data ?? []
  const preferredBranchId = session.getBranchId() ?? user?.homeBranchId ?? null
  const defaultBranchId =
    branchList.find((branch) => branch.id === preferredBranchId)?.id ?? branchList[0]?.id ?? ''

  return (
    <>
      <Link
        to="/customers"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All customers
      </Link>

      <PageHeader
        title="New customer"
        description="Identity capture and customer due diligence. Everything on this screen is captured with the customer present, from the card in their hand."
      />

      {!permissions.canCreate ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
          <ShieldAlert className="size-6 text-warning-text" aria-hidden />
          <p className="text-sm font-medium text-heading">Onboarding is restricted</p>
          <p className="max-w-sm text-xs text-muted">
            Creating a customer needs the {CUSTOMER_PERMISSIONS.create} permission. Ask a supervisor
            to onboard this customer, or to have the permission added to your role.
          </p>
        </div>
      ) : branches.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-11 w-full max-w-2xl" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : branches.isError ? (
        <ErrorSurface
          error={branches.error}
          onRetry={() => void branches.refetch()}
          title="The branch list could not be loaded"
        />
      ) : (
        <CustomerWizard
          branches={branchList}
          defaultBranchId={defaultBranchId}
          onCreated={(customer) => {
            void navigate({ to: '/customers/$customerId', params: { customerId: customer.id } })
          }}
          onCancel={() => {
            void navigate({ to: '/customers' })
          }}
        />
      )}
    </>
  )
}
